// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Exporter resilience — retry, backoff, timeout, and circuit breaker.
 * Mirrors Python provide.telemetry.resilience.
 */
import { _exportFailuresField, _incrementHealth, _recordExportLatency, _registerCircuitStateFn, _retriesField, } from './health';
export class TelemetryTimeoutError extends Error {
    constructor(message) {
        super(message);
        this.name = 'TelemetryTimeoutError';
    }
}
const DEFAULT_POLICY = {
    retries: 0,
    backoffMs: 0,
    timeoutMs: 10000,
    failOpen: true,
};
export const CIRCUIT_BREAKER_THRESHOLD = 3;
export const CIRCUIT_BASE_COOLDOWN_MS = 30000;
const CIRCUIT_MAX_COOLDOWN_MS = 1024000;
// Stryker disable next-line ObjectLiteral
let _policies = {};
// Module-private circuit breaker state — not exported directly to prevent external mutation.
// Use the getter functions below (_get*ForTests) for test introspection.
// Stryker disable next-line ObjectLiteral
const _consecutiveTimeouts = { logs: 0, traces: 0, metrics: 0 };
// Stryker disable next-line ObjectLiteral
const _circuitTrippedAt = { logs: 0, traces: 0, metrics: 0 };
// Stryker disable next-line ObjectLiteral
const _openCount = { logs: 0, traces: 0, metrics: 0 };
/* Stryker disable BooleanLiteral: initial false values are reset by _resetResilienceForTests before each test — equivalent mutant */
// Stryker disable next-line ObjectLiteral
const _halfOpenProbing = {
    logs: false,
    traces: false,
    metrics: false,
};
/* Stryker restore BooleanLiteral */
/** Test-only getters for circuit breaker state inspection. */
export function _getConsecutiveTimeoutsForTests(signal) {
    return _consecutiveTimeouts[signal] ?? 0;
}
export function _getCircuitTrippedAtForTests(signal) {
    return _circuitTrippedAt[signal] ?? 0;
}
export function _getOpenCountForTests(signal) {
    return _openCount[signal] ?? 0;
}
export function _getHalfOpenProbingForTests(signal) {
    return _halfOpenProbing[signal] ?? false;
}
/** Test-only setters for priming circuit breaker state in tests. */
export function _setConsecutiveTimeoutsForTests(signal, value) {
    _consecutiveTimeouts[signal] = value;
}
export function _setCircuitTrippedAtForTests(signal, value) {
    _circuitTrippedAt[signal] = value;
}
export function _setOpenCountForTests(signal, value) {
    _openCount[signal] = value;
}
export function _setHalfOpenProbingForTests(signal, value) {
    _halfOpenProbing[signal] = value;
}
export function setExporterPolicy(signal, policy) {
    _policies[signal] = { ...DEFAULT_POLICY, ...policy };
}
export function getExporterPolicy(signal) {
    return { ...(_policies[signal] ?? DEFAULT_POLICY) };
}
// Stryker disable next-line BlockStatement
function _sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
async function _withTimeout(fn, timeoutMs) {
    // Stryker disable next-line ConditionalExpression,EqualityOperator
    if (timeoutMs <= 0)
        return fn();
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            // Stryker disable next-line StringLiteral: timeout error message content is not tested
            reject(new TelemetryTimeoutError(`operation timed out after ${timeoutMs}ms`));
        }, timeoutMs);
        fn().then((val) => {
            clearTimeout(timer);
            resolve(val);
        }, (err) => {
            clearTimeout(timer);
            reject(err);
        });
    });
}
export async function runWithResilience(signal, fn) {
    const policy = _policies[signal] ?? { ...DEFAULT_POLICY };
    const attempts = Math.max(1, policy.retries + 1);
    // Ensure per-signal dicts are initialized for custom signals.
    // Stryker disable next-line ConditionalExpression: custom signal init — skipping leaves _openCount[signal] as undefined; 2**undefined=NaN makes cooldown NaN which fails < comparison identically
    if (!(signal in _openCount))
        _openCount[signal] = 0;
    // Stryker disable next-line ConditionalExpression,BooleanLiteral: custom signal init — skipping is equivalent since undefined is falsy like false; true init only matters inside circuit-breaker block which requires consecutiveTimeouts >= 3
    if (!(signal in _halfOpenProbing))
        _halfOpenProbing[signal] = false;
    // Circuit breaker check.
    const failField = _exportFailuresField(signal);
    const retryField = _retriesField(signal);
    // Only consult the breaker when timeout enforcement is on. Mirrors Python
    // (resilience.py:177) and Go (resilience.go:170): when timeout=0 the policy
    // explicitly opts out of timeout-driven failure accounting, so the breaker
    // has no signal to act on and must not reject callers.
    // Stryker disable next-line ConditionalExpression
    if (policy.timeoutMs > 0 && _consecutiveTimeouts[signal] >= CIRCUIT_BREAKER_THRESHOLD) {
        // Reject concurrent callers while a half-open probe is already in flight.
        if (_halfOpenProbing[signal]) {
            _incrementHealth(failField);
            if (policy.failOpen)
                return null;
            throw new TelemetryTimeoutError('circuit breaker open: probe in progress');
        }
        const cooldown = Math.min(CIRCUIT_BASE_COOLDOWN_MS * 2 ** _openCount[signal], CIRCUIT_MAX_COOLDOWN_MS);
        const elapsed = Date.now() - _circuitTrippedAt[signal];
        if (elapsed < cooldown) {
            _incrementHealth(failField);
            if (policy.failOpen)
                return null;
            throw new TelemetryTimeoutError('circuit breaker open: too many consecutive timeouts');
        }
        // Half-open: cooldown elapsed — allow one probe.
        _halfOpenProbing[signal] = true;
    }
    let lastError = null;
    for (let attempt = 0; attempt < attempts; attempt++) {
        const started = Date.now();
        try {
            const result = await _withTimeout(fn, policy.timeoutMs);
            _recordExportLatency(signal, Date.now() - started);
            if (_halfOpenProbing[signal]) {
                _halfOpenProbing[signal] = false;
                _consecutiveTimeouts[signal] = 0;
                _openCount[signal] = Math.max(0, _openCount[signal] - 1);
                // Stryker disable next-line BlockStatement: else-block body on non-probe success — removing is equivalent since timeouts are already 0 on fresh closed circuit
            }
            else {
                _consecutiveTimeouts[signal] = 0;
            }
            return result;
        }
        catch (err) {
            lastError = err instanceof Error ? err : new Error(String(err));
            _incrementHealth(failField);
            if (err instanceof TelemetryTimeoutError) {
                if (_halfOpenProbing[signal]) {
                    _halfOpenProbing[signal] = false;
                    _openCount[signal] += 1;
                    _circuitTrippedAt[signal] = Date.now();
                }
                else {
                    _consecutiveTimeouts[signal] = (_consecutiveTimeouts[signal] ?? 0) + 1;
                    // Stryker disable next-line ConditionalExpression,EqualityOperator
                    if (_consecutiveTimeouts[signal] >= CIRCUIT_BREAKER_THRESHOLD) {
                        _openCount[signal] += 1;
                        _circuitTrippedAt[signal] = Date.now();
                    }
                }
            }
            else if (_halfOpenProbing[signal]) {
                _halfOpenProbing[signal] = false;
                _openCount[signal] += 1;
                _circuitTrippedAt[signal] = Date.now();
            }
            else {
                _consecutiveTimeouts[signal] = 0;
            }
            // Stryker disable next-line ArithmeticOperator
            if (attempt < attempts - 1) {
                _incrementHealth(retryField);
                // Stryker disable next-line ConditionalExpression,EqualityOperator
                if (policy.backoffMs > 0)
                    await _sleep(policy.backoffMs);
            }
        }
    }
    if (policy.failOpen)
        return null;
    // Stryker disable next-line StringLiteral: unreachable fallback — lastError is always set by the catch block above
    /* v8 ignore next */
    throw lastError ?? new Error('all retry attempts failed');
}
export function getCircuitState(signal) {
    const openCount = _openCount[signal] ?? 0;
    if (_halfOpenProbing[signal]) {
        return { state: 'half-open', openCount, cooldownRemainingMs: 0 };
    }
    if ((_consecutiveTimeouts[signal] ?? 0) >= CIRCUIT_BREAKER_THRESHOLD) {
        const cooldown = Math.min(CIRCUIT_BASE_COOLDOWN_MS * 2 ** openCount, CIRCUIT_MAX_COOLDOWN_MS);
        const remaining = cooldown - (Date.now() - _circuitTrippedAt[signal]);
        // Stryker disable next-line EqualityOperator: > 0 vs >= 0 — exact millisecond boundary P≈0
        if (remaining > 0) {
            return { state: 'open', openCount, cooldownRemainingMs: remaining };
        }
        return { state: 'half-open', openCount, cooldownRemainingMs: 0 };
    }
    return { state: 'closed', openCount, cooldownRemainingMs: 0 };
}
// Register with health module to break circular dependency.
_registerCircuitStateFn(getCircuitState);
export function _resetResilienceForTests() {
    _policies = {};
    for (const k of ['logs', 'traces', 'metrics']) {
        _consecutiveTimeouts[k] = 0;
        _circuitTrippedAt[k] = 0;
        _openCount[k] = 0;
        _halfOpenProbing[k] = false;
    }
}
